import Container from '@/components/atoms/Container';
import Icon from '@/components/atoms/Icon';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from '@/styles/address.module.scss';
import Link from 'next/link';
import {Fragment, memo} from 'react';
import {COUNTRY_FLAGS} from '@/constants/images';
import {CONTACT_EMAIL} from '@/constants/urls';

interface Office {
    country: string;
    flag: string;
    flagAlt: string;
    company: string;
    /** Address lines, each rendered as its own <span> (a part ending in a space joins the next one directly). */
    address: string[];
    phone: string;
    phoneHref: string;
}

const US_PHONE = {phone: '+1-708-515-4004', phoneHref: 'tel:+1-708-515-4004'};

const OFFICES: Office[] = [
    {
        country: 'USA',
        flag: COUNTRY_FLAGS.usa,
        flagAlt: 'USA flag',
        company: 'Debut Infotech Global Technologies LLC',
        address: ['2501 Chatham,', 'Rd Suite R', 'Springfield,', 'IL 62704'],
        ...US_PHONE
    },
    {
        country: 'UK',
        flag: COUNTRY_FLAGS.uk,
        flagAlt: 'UK flag',
        company: 'Debut Infotech Pvt Ltd',
        address: ['7 Pound Close,', 'Yarnton,', 'Oxfordshire,', 'OX51QG'],
        phone: '+44-770-304-0079',
        phoneHref: 'tel:+44-770-304-0079'
    },
    {
        country: 'Canada',
        flag: COUNTRY_FLAGS.canada,
        flagAlt: 'Canada flag',
        company: 'Debut Infotech Pvt Ltd',
        address: ['154 Eden Oak Trail,', 'Kitchener,', 'ON', 'N2A 0H9'],
        ...US_PHONE
    },
    {
        country: 'INDIA',
        flag: COUNTRY_FLAGS.india,
        flagAlt: 'India flag',
        company: 'Debut Infotech Pvt Ltd',
        address: ['Sector 101-A, Plot No: I-42, IT City Rd, ', 'JLPL Industrial Area, Mohali,', 'PB', '140306'],
        phone: '9888402396',
        phoneHref: 'tel:9888402396'
    }
];

const OfficeCard = ({office}: {office: Office}) => (
    <div className={styles.countries_wrapper}>
        <div className={styles.heading_logo}>
            <p className={`${styles.body1} ${styles.black} ${styles.countries_heading} font-headers`}>{office.country}</p>
            <OptimizedImage src={office.flag} alt={office.flagAlt} height={28} width={28} sizes="28px" unoptimized />
        </div>
        <div>
            <div className={`${styles.body1} ${styles.countries_sub_heading} ${styles.office_address} font-regular-text`}>
                {office.company}
            </div>
        </div>
        <p className={`${styles.body1} ${styles.black} ${styles.countries_sub_heading} ${styles.office_address} font-regular-text`}>
            {office.address.map((part, index) => (
                <Fragment key={part}>
                    {index > 0 && !office.address[index - 1].endsWith(' ') ? ' ' : null}
                    <span>{part}</span>
                </Fragment>
            ))}
        </p>
        <Link href={office.phoneHref} className="menu-links">
            <div className={styles.call_icon_wrapper}>
                <Icon name="Call" className={styles.call_icon} />
                <p className={`${styles.body1} ${styles.black} ${styles.countries_contact} font-regular-text`}>{office.phone}</p>
            </div>
        </Link>
        <Link href={`mailto:${CONTACT_EMAIL}`} className="menu-links" target="_blank">
            <div className={styles.mail_wrapper}>
                <Icon name="Email" className={styles.email_icon} />
                <p className={`${styles.body1} ${styles.black} ${styles.countries_mail} font-regular-text`}>{CONTACT_EMAIL}</p>
            </div>
        </Link>
    </div>
);

const OfficeAddress = (props: any): any => {
    const {className} = props;
    return (
        <div className={`${styles.address_section} ${className}`}>
            <div className={styles.countries}>
                <Container>
                    <div className={styles.grid}>
                        {OFFICES.map((office) => (
                            <div key={office.country} className={styles.grid_item}>
                                <OfficeCard office={office} />
                            </div>
                        ))}
                    </div>
                </Container>
            </div>
        </div>
    );
};

export default memo(OfficeAddress);
